import React, { useCallback, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import ChartPanel from './shared/ChartPanel';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import DateRangeFilter, { getPresetRange } from './shared/DateRangeFilter';
import { axisProps, tooltipStyle, barColor } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// What gets prescribed most. Names are counted as the doctor wrote them (the prescription keeps
// its own snapshot of the name), with a flag for whether the medicine is in the pharmacy's
// inventory or an outside prescription.

const COLUMNS = [
  { key: 'medicine', label: 'Medicine' },
  { key: 'rxLines', label: 'Prescriptions', align: 'right' },
  { key: 'qtyPrescribed', label: 'Qty Prescribed', align: 'right' },
  { key: 'qtyDispensed', label: 'Qty Dispensed', align: 'right' },
  { key: 'uniquePatients', label: 'Unique Patients', align: 'right' },
  {
    key: 'inInventory', label: 'In Inventory',
    render: (r) =>
      r.inInventory ? (
        <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700">Yes</span>
      ) : (
        <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600">External</span>
      ),
    csv: (r) => (r.inInventory ? 'Yes' : 'External'),
  },
];

const TopMedicinesReport = () => {
  const drill = useDrill();
  const [range, setRange] = useState(() => getPresetRange('thisMonth'));
  const [preset, setPreset] = useState('thisMonth');
  const [inventoryOnly, setInventoryOnly] = useState(false);

  const fetcher = useCallback(
    () => reportsAPI.getTopMedicines({ from: range.from, to: range.to, limit: 30, inventoryOnly }),
    [range, inventoryOnly]
  );
  const { data, loading } = useReport(fetcher);

  const top15 = (data?.rows || []).slice(0, 15).map((r) => ({ ...r, rxLines: Number(r.rxLines) }));

  return (
    <div className="space-y-5">
      <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />

      <div className="flex items-center gap-3">
        <label className="bg-white rounded-xl shadow px-4 py-2.5 flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={inventoryOnly}
            onChange={(e) => setInventoryOnly(e.target.checked)}
            className="w-4 h-4 accent-emerald-600"
          />
          Inventory medicines only
        </label>
        <div className="ml-auto"><PrintButton /></div>
      </div>

      <ChartPanel title="Top 15 prescribed medicines">
        {loading || top15.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-16">
            {loading ? 'Loading' : 'No medicines prescribed in the selected range'}
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(220, top15.length * 30)}>
            <BarChart data={top15} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" horizontal={false} />
              <XAxis type="number" {...axisProps} allowDecimals={false} />
              <YAxis type="category" dataKey="medicine" {...axisProps} width={180} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="rxLines" name="Prescriptions" radius={[0, 4, 4, 0]} barSize={16}>
              {top15.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
            </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'medicine', value: r.medicine, from: range.from, to: range.to })}
        title="Prescribed medicines"
        columns={COLUMNS}
        rows={data?.rows || []}
        csvName={`top-medicines-${range.from}-to-${range.to}`}
      />

      <ReportPrintLayout
        title="Top Prescribed Medicines"
        subtitle={`Period: ${range.from} to ${range.to}${inventoryOnly ? ' · inventory only' : ''}`}
        tables={[{ title: 'Prescribed medicines', columns: COLUMNS, rows: data?.rows || [] }]}
      />

      {drill.node}
    </div>
  );
};

export default TopMedicinesReport;
